/* tutor-1 (C): EL HILO. Se desplaza POR DENTRO (el resto de la pagina no se mueve).

   ARCHIVO EN ASCII A PROPOSITO.

   alumno-m4 (5.2, pedido de Carlos del 22-oct): AL MANDAR, LA PREGUNTA ARRIBA. Antes el hilo seguia el final mientras
   llegaba texto (si el alumno estaba abajo) y la respuesta larga se leia "desde abajo". Desde hoy:
   - al MANDAR (llega una burbuja del alumno nueva y `contestando` esta encendido), el hilo se desplaza para que esa
     burbuja quede ARRIBA, con su borde superior a `AIRE_ARRIBA_PX` del borde del hilo, y ahi se queda;
   - mientras la respuesta llega, el hilo NO se mueve solo: el alumno baja cuando quiere;
   - para que la ultima burbuja PUEDA quedar arriba aunque la respuesta sea corta, al final va un espaciador de alto
     igual al alto visible del hilo menos el alto de esa burbuja (nunca negativo), que se recalcula cuando el hilo
     cambia de tamano (teclado del telefono, ventana);
   - abrir una conversacion (al montar sin estar contestando, o cuando cambia `clave`) va al final, como antes; el
     titulo, las fuentes, los chips o el `fin` no mueven nada;
   - el salto es siempre inmediato (sin animacion), asi que "reducir movimiento" no necesita rama propia.
   La burbuja se encuentra por su `data-testid` (`tutor-mensaje-alumno`); el hilo es `relative` para que su
   `offsetTop` se mida desde el.

   camara-pills (seccion 3, pedido de Carlos del 23-oct): EL HUECO MIDE SOLO LO QUE FALTA. El espaciador ya no es fijo
   (alto visible menos la burbuja): mide lo que falta para que, con la burbuja a `AIRE_ARRIBA_PX` del borde, el
   contenido desde ella hasta el fondo del ultimo hijo real (lo que haya antes del espaciador) llene el alto visible:
   `max(0, clientHeight - AIRE_ARRIBA_PX - (fondo del ultimo hijo - burbuja.offsetTop))`. Mientras la respuesta crece,
   el hueco se encoge lo mismo y el alto total no cambia (la pantalla no se mueve); cuando la respuesta pasa del alto
   visible, el hueco es 0 y sale del flujo. Se recalcula en cada render con burbuja ancla, cuando el hilo cambia de
   tamano y cuando el contenido cambia o crece (un `MutationObserver` del hilo y el `ResizeObserver` sobre el ultimo
   hijo real: las formulas y las imagenes crecen despues del render). Esas recalculadas JAMAS escriben `scrollTop`. */
import React, { useEffect, useLayoutEffect, useRef } from 'react';

export const AIRE_ARRIBA_PX = 8;
const BURBUJA = '[data-testid="tutor-mensaje-alumno"]';

export default function Hilo({ children, marca, contestando = false, clave = null }) {
  const ref = useRef(null);
  const espacioRef = useRef(null);
  const cuentaRef = useRef(null);
  const claveRef = useRef(clave);
  const anclaRef = useRef(null);
  const tamanoRef = useRef(null);
  const vigiladoRef = useRef(null);

  /** El ResizeObserver sigue al ultimo hijo real (cambia cuando llega la respuesta o los chips). */
  const vigilarUltimo = (ultimo) => {
    const ro = tamanoRef.current;
    if (!ro || vigiladoRef.current === ultimo) return;
    if (vigiladoRef.current) ro.unobserve(vigiladoRef.current);
    if (ultimo) ro.observe(ultimo);
    vigiladoRef.current = ultimo;
  };

  const ajustarEspacio = () => {
    const el = ref.current;
    const esp = espacioRef.current;
    if (!el || !esp) return;
    const burbuja = anclaRef.current;
    const ultimo = esp.previousElementSibling;
    vigilarUltimo(ultimo);
    let alto = 0;
    if (burbuja && el.contains(burbuja)) {
      const fondo = ultimo ? ultimo.offsetTop + ultimo.offsetHeight : burbuja.offsetTop + burbuja.offsetHeight;
      alto = Math.max(0, el.clientHeight - AIRE_ARRIBA_PX - (fondo - burbuja.offsetTop));
    }
    // Sin alto, fuera del flujo: asi no suma el `gap` del hilo al final de una conversacion abierta.
    esp.style.height = `${alto}px`;
    esp.style.display = alto > 0 ? 'block' : 'none';
  };

  const alFinal = () => {
    const el = ref.current;
    anclaRef.current = null;
    ajustarEspacio();
    if (el) el.scrollTop = el.scrollHeight;
  };

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const burbujas = el.querySelectorAll(BURBUJA);
    const n = burbujas.length;
    const previo = cuentaRef.current;
    const otraConversacion = claveRef.current !== clave;
    cuentaRef.current = n;
    claveRef.current = clave;
    const mando = contestando && n > 0 && (previo === null || n > previo);
    if (mando) {
      const burbuja = burbujas[n - 1];
      anclaRef.current = burbuja;
      ajustarEspacio();
      el.scrollTop = Math.max(0, burbuja.offsetTop - AIRE_ARRIBA_PX);
      return;
    }
    if (previo === null || otraConversacion || (n !== previo && !contestando)) alFinal();
    else if (anclaRef.current) ajustarEspacio();
  }, [marca, contestando, clave]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const alCambiarDeTamano = () => ajustarEspacio();
    let observador = null;
    if (typeof ResizeObserver !== 'undefined') {
      observador = new ResizeObserver(alCambiarDeTamano);
      observador.observe(el);
      tamanoRef.current = observador;
    }
    let contenido = null;
    if (typeof MutationObserver !== 'undefined') {
      contenido = new MutationObserver(alCambiarDeTamano);
      contenido.observe(el, { childList: true, subtree: true, characterData: true });
    }
    window.addEventListener('resize', alCambiarDeTamano);
    return () => {
      if (observador) observador.disconnect();
      if (contenido) contenido.disconnect();
      tamanoRef.current = null;
      vigiladoRef.current = null;
      window.removeEventListener('resize', alCambiarDeTamano);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div ref={ref}
      className="relative flex-1 min-h-0 overflow-y-auto flex flex-col gap-[18px] md:gap-[22px] pt-1.5 pb-[18px] px-0.5"
      style={{ scrollbarWidth: 'thin', scrollbarColor: '#2a2a2a transparent' }}
      data-testid="tutor-hilo">
      {children}
      <div ref={espacioRef} className="shrink-0" style={{ height: 0, display: 'none' }} aria-hidden="true"
        data-testid="tutor-hilo-espacio" />
    </div>
  );
}
